import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "./anim";

const SERVICES = [
  {
    title: "Business Strategy",
    body: "Positioning, growth planning, and decision frameworks that help you move with intent instead of instinct.",
  },
  {
    title: "Investment Opportunity Analysis",
    body: "An objective, experienced evaluation of the opportunity in front of you: the numbers, the risks, and the realistic outcomes.",
  },
  {
    title: "Deal Evaluation",
    body: "Structure, terms, and downside exposure. Know exactly what you are agreeing to before you sign.",
  },
  {
    title: "Wealth-Building Strategies",
    body: "Long-term, principle-driven strategies tailored to your goals, your resources, and your timeline.",
  },
  {
    title: "Entrepreneurial Growth",
    body: "Strategic guidance and accountability for founders who want to grow deliberately, not accidentally.",
  },
];

const CHAPTERS = [
  { title: "Clarity before action", body: "We slow the moment down so you can move faster after." },
  { title: "Objective outside perspective", body: "Real-world experience and an independent view, free from bias." },
  { title: "Calculated risk, not guesswork", body: "See the numbers, the risks, and the outcomes before you commit." },
  { title: "Strategy built around you", body: "No templates. Every plan fits your goals, resources, and timeline." },
];

export default function WhatWeDo() {
  return (
    <section id="what-we-do" data-testid="what-we-do-section" className="border-t border-white/10 bg-[#0A0A0D]">
      <div className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
        <SectionHeading
          overline="What We Do"
          title="Consulting that turns opportunity into strategy."
          copy="Five focused disciplines. One goal: helping you make smarter decisions with your money, your business, and your future."
        />

        <div className="mt-16 border-t border-white/10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06} y={24}>
              <div
                data-testid={`service-row-${i + 1}`}
                className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 border-b border-white/10 py-8 transition-colors duration-500 hover:bg-white/[0.025] sm:gap-10 sm:px-4"
              >
                <span className="w-12 text-sm font-medium tracking-[0.2em] text-[#71717A] transition-colors duration-500 group-hover:text-[#C5A059]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="text-xl font-light tracking-tight text-[#F9F8F6] transition-colors duration-500 group-hover:text-[#C5A059] sm:text-2xl">
                    {s.title}
                  </h3>
                  <p className="mt-2 max-w-2xl text-sm font-light leading-relaxed text-[#A1A1AA]">{s.body}</p>
                </div>
                <ArrowUpRight
                  size={20}
                  className="text-[#71717A] opacity-0 transition-[opacity,color,transform] duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#C5A059] group-hover:opacity-100"
                  aria-hidden="true"
                />
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div id="approach" data-testid="approach-strip" className="mt-20">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#C5A059]">Our Approach</p>
            <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
              {CHAPTERS.map((c, i) => (
                <div key={c.title} data-testid={`approach-chapter-${i + 1}`} className="group bg-[#0A0A0D] p-7 transition-colors duration-500 hover:bg-[#0F0F12]">
                  <span className="text-outline-gold text-4xl font-semibold" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 text-base font-medium tracking-tight text-[#F9F8F6]">{c.title}</h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-[#A1A1AA]">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
